import React from 'react';

const MessageContent = ({ text }) => {
  // Function to parse markdown and convert to React elements
  const parseMarkdown = (content) => {
    const elements = [];
    const lines = content.split('\n');
    let currentList = null;
    let listItems = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check for numbered list items (e.g., "1. ", "2. ")
      const numberedListMatch = line.match(/^(\d+)\.\s+(.*)$/);
      if (numberedListMatch) {
        const listContent = parseInlineMarkdown(numberedListMatch[2]);
        listItems.push({ type: 'numbered', content: listContent });
        currentList = 'numbered';
        continue;
      }
      
      // Check for bullet list items (e.g., "- ", "* ")
      const bulletListMatch = line.match(/^[-*]\s+(.*)$/);
      if (bulletListMatch) {
        const listContent = parseInlineMarkdown(bulletListMatch[1]);
        listItems.push({ type: 'bullet', content: listContent });
        currentList = 'bullet';
        continue;
      }
      
      // If we were building a list and hit a non-list line, finish the list
      if (currentList && listItems.length > 0) {
        elements.push({
          type: currentList === 'numbered' ? 'ol' : 'ul',
          items: [...listItems]
        });
        listItems = [];
        currentList = null;
      }
      
      // Handle regular paragraphs
      if (line.trim()) {
        elements.push({
          type: 'paragraph',
          content: parseInlineMarkdown(line)
        });
      } else if (elements.length > 0 && elements[elements.length - 1].type === 'paragraph') {
        // Add line break for empty lines between paragraphs
        elements.push({ type: 'break' });
      }
    }
    
    // Don't forget to add any remaining list items
    if (currentList && listItems.length > 0) {
      elements.push({
        type: currentList === 'numbered' ? 'ol' : 'ul',
        items: listItems
      });
    }
    
    return elements;
  };
  
  // Parse inline markdown (bold, links, etc.)
  const parseInlineMarkdown = (text) => {
    const parts = [];
    let remaining = text;
    let lastIndex = 0;
    
    // Combined regex for all inline elements
    const inlineRegex = /(\*\*([^*]+)\*\*)|(\[([^\]]+)\]\(([^)]+)\))/g;
    const matches = [];
    let match;
    
    while ((match = inlineRegex.exec(text)) !== null) {
      matches.push({
        index: match.index,
        length: match[0].length,
        type: match[1] ? 'bold' : 'link',
        content: match[1] ? match[2] : match[4],
        url: match[5]
      });
    }
    
    matches.forEach((matchInfo) => {
      // Add text before the match
      if (matchInfo.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, matchInfo.index)
        });
      }
      
      // Add the matched element
      if (matchInfo.type === 'bold') {
        parts.push({
          type: 'bold',
          content: matchInfo.content
        });
      } else if (matchInfo.type === 'link') {
        parts.push({
          type: 'link',
          text: matchInfo.content,
          url: matchInfo.url
        });
      }
      
      lastIndex = matchInfo.index + matchInfo.length;
    });
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    // If no matches were found, return the original text
    if (parts.length === 0) {
      return [{ type: 'text', content: text }];
    }
    
    return parts;
  };
  
  // Render inline elements
  const renderInline = (parts, key = '') => {
    return parts.map((part, index) => {
      const uniqueKey = `${key}-${index}`;
      
      if (part.type === 'text') {
        return <span key={uniqueKey}>{part.content}</span>;
      }
      
      if (part.type === 'bold') {
        return <strong key={uniqueKey} style={{ fontWeight: 600 }}>{part.content}</strong>;
      }
      
      if (part.type === 'link') {
        return (
          <a
            key={uniqueKey}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#1890ff',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
            onClick={(e) => {
              e.stopPropagation();
              window.open(part.url, '_blank', 'noopener,noreferrer');
              e.preventDefault();
            }}
          >
            {part.text}
          </a>
        );
      }
      
      return null;
    });
  };
  
  const parsedContent = parseMarkdown(text);
  
  return (
    <div style={{ width: '100%' }}>
      {parsedContent.map((element, index) => {
        if (element.type === 'paragraph') {
          return (
            <p key={index} style={{ margin: '0 0 8px 0', lineHeight: 1.5 }}>
              {renderInline(element.content, `p-${index}`)}
            </p>
          );
        }
        
        if (element.type === 'ol') {
          return (
            <ol key={index} style={{ 
              margin: '8px 0 8px 20px', 
              paddingLeft: '20px',
              lineHeight: 1.6 
            }}>
              {element.items.map((item, itemIndex) => (
                <li key={itemIndex} style={{ marginBottom: '6px' }}>
                  {renderInline(item.content, `ol-${index}-${itemIndex}`)}
                </li>
              ))}
            </ol>
          );
        }
        
        if (element.type === 'ul') {
          return (
            <ul key={index} style={{ 
              margin: '8px 0 8px 20px', 
              paddingLeft: '20px',
              lineHeight: 1.6 
            }}>
              {element.items.map((item, itemIndex) => (
                <li key={itemIndex} style={{ marginBottom: '6px' }}>
                  {renderInline(item.content, `ul-${index}-${itemIndex}`)}
                </li>
              ))}
            </ul>
          );
        }
        
        if (element.type === 'break') {
          return <br key={index} />;
        }
        
        return null;
      })}
    </div>
  );
};

export default MessageContent;